import { useEffect, useState } from 'preact/hooks';
import { useCloudpack } from '../components/CloudpackProvider/CloudpackProvider.js';

export type PageLoadTimeReport =
  | {
      pageLoadTime: number;
      status: 'success';
    }
  | {
      status: 'error';
    }
  | {
      status: 'detecting';
    };

/**
 * Hook that reports the page load time to the Cloudpack backend.
 * @returns The page load time in milliseconds, or  `null` if the page load time could not be determined. `undefined` is returned if the page load time is still being calculated.
 */
export function usePageLoadTimeReporter(): PageLoadTimeReport {
  const [report, setReport] = useState<PageLoadTimeReport>({ status: 'detecting' });
  const cloudpack = useCloudpack();

  useEffect(() => {
    async function reportPageLoadTime() {
      if (!window.__cloudpack) {
        throw new Error('Cloudpack not found on window');
      }
      const { getPageLoadTime, getBrowserCacheRatio, pageSessionContext } = window.__cloudpack;

      if (!getPageLoadTime) {
        throw new Error('getPageLoadTime not found on window.__cloudpack');
      }

      if (!pageSessionContext) {
        throw new Error('pageSessionContext not found on window.__cloudpack');
      }

      if (!getBrowserCacheRatio) {
        throw new Error('getBrowserCacheRatio not found on window.__cloudpack');
      }

      const newPageLoadTime = await getPageLoadTime()
        .then((value) => {
          setReport({ pageLoadTime: value, status: 'success' });
          return value;
        })
        .catch((err) => {
          console.error('[Cloudpack] Failed to get page load time', err);
          setReport({ status: 'error' });
          return null;
        });

      // Page is loaded, PLT and the browser cache ratio can be reported
      const browserCache = getBrowserCacheRatio();
      await cloudpack.reportSpan.mutate({
        name: 'PAGE_LOAD_TIME',
        // OpenTelemetry doesn't care about the actual start time, start and end time is used to calculate the duration
        startTime: 0,
        // In case the performance entry is not found, the end time will be 0.
        // Unfortunately, it is not possible to report a span without an endTime.
        endTime: newPageLoadTime ?? 0,
        attributes: {
          path: pageSessionContext.requestPath,
          browserCacheHitCount: browserCache.hit,
          browserCacheTotalCount: browserCache.total,
          browserCacheRatio: browserCache.ratio,
        },
      });
    }

    void reportPageLoadTime();
  }, [cloudpack]);

  return report;
}
